import type { ReactElement } from 'react';
import type { Player } from '@/types/domain';

interface PlayerGraphProps {
  player?: Player;
  view?: string;
  totalPlayed?: number;
  type?: string;
}

interface PlayerGraphComponent {
  (props: PlayerGraphProps): ReactElement;
  defaultPropTypes?: {
    player: Player;
    view: string;
    totalPlayed: number;
  };
}

const PlayerGraph: PlayerGraphComponent = (props: PlayerGraphProps) => {
  const { player, view, totalPlayed } = props;

  const totalPlayingTime = player?.playingTime as number | undefined;
  const totalSubstituteTime = player?.substitutionTime as number | undefined;
  /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
  const totalAbsentTime =
    totalPlayed! - (totalPlayingTime! + totalSubstituteTime!);
  /* eslint-enable @typescript-eslint/no-non-null-assertion */

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const playingBarWidth = (100 / totalPlayed!) * totalPlayingTime!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const substituteBarWidth = (100 / totalPlayed!) * totalSubstituteTime!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const absentBarWidth = (100 / totalPlayed!) * totalAbsentTime;

  return (
    <>
      <div className="PlayerGraph">
        <div
          style={{ width: `${String(playingBarWidth)}%` }}
          className={`PlayerGraph-bar ${totalPlayingTime === 0 ? '' : 'PlayerGraph-bar--playing'} ${
            playingBarWidth < 21 ? 'not-padding' : ''
          }`}
        >
          {view === 'percentage' && playingBarWidth > 0 && (
            <span className="PlayerGraph-value">
              {Math.round(playingBarWidth)}%
            </span>
          )}
          {view === 'minutes' && totalPlayingTime !== 0 && (
            <span className="PlayerGraph-value">{totalPlayingTime}</span>
          )}
        </div>

        {totalSubstituteTime !== 0 && (
          <div
            style={{
              width:
                playingBarWidth === 0
                  ? `${String(substituteBarWidth)}%`
                  : `calc(${String(substituteBarWidth)}% + 20px)`,
            }}
            className={`PlayerGraph-bar PlayerGraph-bar--substitute ${playingBarWidth === 0 ? 'is-zero' : ''}`}
          >
            {view === 'percentage' && substituteBarWidth > 10 && (
              <span className="PlayerGraph-value">
                {Math.round(substituteBarWidth)}%
              </span>
            )}
            {view === 'minutes' && substituteBarWidth > 10 && (
              <span className="PlayerGraph-value">{totalSubstituteTime}</span>
            )}
          </div>
        )}
        {totalAbsentTime !== 0 && (
          <div
            style={{
              width:
                playingBarWidth === 0
                  ? `${String(absentBarWidth)}%`
                  : `calc(${String(absentBarWidth)}% + 10px)`,
            }}
            className={`PlayerGraph-bar PlayerGraph-bar--absent`}
          >
            {view === 'percentage' && substituteBarWidth > 0 && (
              <span className="PlayerGraph-value">
                {Math.round(absentBarWidth)}%
              </span>
            )}
            {view === 'minutes' && absentBarWidth > 10 && (
              <span className="PlayerGraph-value">{totalAbsentTime}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

PlayerGraph.defaultPropTypes = {
  player: {},
  view: 'minutes',
  totalPlayed: 0,
};

export default PlayerGraph;
